import { ApolloError, ApolloQueryResult } from '@apollo/client';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import * as Rx from 'rxjs';
import { delay, filter, flatMap, map, switchMap, tap } from 'rxjs/operators';

import { client } from '../../../async/utils/client';
import {
  SearchDocumentType,
  SearchEverythingQuery,
  SearchEverythingQueryVariables,
  SearchFilters,
  SearchPostBody,
  SearchQuestionBody,
  SearchSortOrder,
  useSearchEverythingQuery,
} from '../../../graphql/gen/types';
import { SEARCH_EVERYTHING } from '../../../graphql/queries/SearchEverything';
import { useSelectedLabelIds } from '../../../models/filters/useSelectedLabelIds';
import { usePlexes, useSelectedPlexIds } from '../../../models/filters/useSelectedPlexIds';
import { useSearchFilters, useSearchSortOrder } from '../../../redux';
import { FetchMoreCallback, useInfiniteScroll } from '../feed/useInfiniteScroll';
import { logAnalytics } from '../shared/logAnalytics';

export type SearchResultOption = { id: string; body: SearchPostBody | SearchQuestionBody };
export const ALL_DOCUMENT_TYPES = [
  SearchDocumentType.Post,
  SearchDocumentType.Question,
  SearchDocumentType.Event,
  SearchDocumentType.Milestone,
  SearchDocumentType.Task,
];
type QueryState = { query: string; filters: SearchFilters; sortOrder: SearchSortOrder };
export type SearchState =
  | { state: 'initial' }
  | { state: 'more_input_needed' }
  | { state: 'can_load' }
  | { state: 'loading' }
  | { state: 'done' | 'loading_more'; options: SearchResultOption[]; nextCursor: string | null | undefined }
  | { state: 'error'; error: string };
type QueryStreamState = Rx.Subject<QueryState>;
const SEARCH_DEBOUNCE_TIME_MS = 500;
const EMPTY_FILTER: SearchFilters = {
  plexIds: [],
  types: [],
  labelIds: [],
};
const EMPTY_STATE: QueryState = { query: '', filters: EMPTY_FILTER, sortOrder: SearchSortOrder.Score };

interface SearchQuery {
  input: string;
}

export type Value = SearchState;
export type Setter = (args: { input: string }) => void;

export function useSearch(
  minQueryTextLength = 3,
  containerRef: RefObject<HTMLDivElement> | null = null,
  useFilters = true
): [Value, Setter] {
  const plexIds = useSelectedPlexIds();
  const plexes = usePlexes();
  const labelIds = useSelectedLabelIds();
  const sortOrder = useSearchSortOrder();
  const typeFilters = useSearchFilters();

  const filters: SearchFilters = useMemo(
    () =>
      useFilters
        ? { plexIds, types: typeFilters, labelIds }
        : { plexIds: plexes ? plexes.map((p) => p.id) : [], types: ALL_DOCUMENT_TYPES, labelIds: [] },
    [labelIds, plexIds, plexes, typeFilters, useFilters]
  );

  const sortOrderOrDefault = useMemo(() => (useFilters ? sortOrder : SearchSortOrder.Score), [sortOrder, useFilters]);

  const options = useMemo(() => ({ minQueryTextLength, filters, sortOrder: sortOrderOrDefault }), [
    minQueryTextLength,
    filters,
    sortOrderOrDefault,
  ]);

  const [searchResult, setSearchQuery] = useSearchAdvanced(containerRef, options);

  return [searchResult, setSearchQuery];
}

export function useSearchAdvanced(
  containerRef: RefObject<HTMLDivElement> | null = null,
  {
    minQueryTextLength,
    filters,
    sortOrder,
  }: {
    minQueryTextLength: number;
    filters: SearchFilters;
    sortOrder: SearchSortOrder;
  }
): [Value, Setter, SearchQuery] {
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    input: '',
  });
  const [queryStream] = useState<QueryStreamState>(new Rx.BehaviorSubject(EMPTY_STATE));
  const [searchResult, setSearch] = useState<SearchState>({ state: 'initial' });
  const { fetchMore } = useSearchEverythingQuery({ fetchPolicy: 'standby' });

  const nextCursor =
    searchResult.state === 'done' || searchResult.state === 'loading_more' ? searchResult.nextCursor : null;

  const onFetchMore: FetchMoreCallback = useCallback(
    async (isCancelled): Promise<void> => {
      setSearch((prevSearch) => (prevSearch.state === 'done' ? { ...prevSearch, state: 'loading_more' } : prevSearch));

      const fetchMoreResult = await fetchMore({
        variables: {
          query: searchQuery.input,
          cursor: nextCursor,
          filters,
          sortOrder,
        },
      });

      if (!isCancelled()) {
        const newSearch = fetchMoreResult?.data?.currentUser?.domain?.search;

        if (newSearch != null) {
          setSearch((prevSearch) =>
            prevSearch.state === 'loading_more'
              ? {
                  state: 'done',
                  options: [...prevSearch.options, ...newSearch.items],
                  nextCursor: newSearch.nextCursor,
                }
              : prevSearch
          );
        }
      }
    },
    [fetchMore, filters, nextCursor, searchQuery, sortOrder]
  );

  useInfiniteScroll({
    containerRef,
    onFetchMore,
    canFetchMore: nextCursor != null,
  });

  /*
  Handle `queryStream` events, computing search state, debouncing, triggering queries to
  the server and setting the response state
  */
  useEffect(() => {
    const subscription = queryStream
      .pipe(
        // Use `switchMap` to handle only the latest `query` and cancel handling of previous queries
        switchMap(({ query, filters, sortOrder }: QueryState) =>
          Rx.from([{ query }]).pipe(
            // Compute equivalent search state
            map(({ query }): {
              query: string;
              search: SearchState;
            } => {
              if (query.length === 0) {
                return { query, search: { state: 'initial' } };
              } else if (query.length < minQueryTextLength) {
                return { query, search: { state: 'more_input_needed' } };
              } else {
                return { query, search: { state: 'can_load' } };
              }
            }),
            // Synchronize with React
            tap(({ search }) => {
              setSearch(search);
            }),
            // Filter only events which could trigger a load
            filter(({ search }) => search.state === 'can_load'),
            // Delay events to simulate debounce behavior.
            // If newer `query` is emitted from `queryStream`, this will not emit further due to `switchMap`
            delay(SEARCH_DEBOUNCE_TIME_MS),
            // Synchronize with React
            tap(() => {
              setSearch({ state: 'loading' });
            }),
            // Trigger request to server
            flatMap(({ query }) =>
              Rx.from(
                client
                  .query<SearchEverythingQuery, SearchEverythingQueryVariables>({
                    query: SEARCH_EVERYTHING,
                    variables: { query, filters, sortOrder },
                  })
                  .then((res) => {
                    logAnalytics({ name: 'search query submitted' });
                    return res;
                  })
              )
            ),
            // Synchronize response with React
            tap(
              (result: ApolloQueryResult<SearchEverythingQuery>) => {
                setSearch({
                  state: 'done',
                  options: result.data?.currentUser?.domain?.search?.items || [],
                  nextCursor: result.data?.currentUser?.domain?.search?.nextCursor,
                });
              },
              (error: ApolloError) => {
                setSearch({
                  state: 'error',
                  error: error.message,
                });
              }
            )
          )
        )
      )
      // Start execution of this observable
      .subscribe();

    const cleanupEffect = (): void => {
      // Cancel execution of this observable
      subscription.unsubscribe();
      queryStream.complete();
    };
    return cleanupEffect;
  }, [minQueryTextLength, queryStream]);

  /*
  Publish updates to `query` to the subscriber of `queryStream`
  */
  useEffect(() => {
    queryStream.next({ query: searchQuery.input, filters, sortOrder });
  }, [filters, queryStream, searchQuery, sortOrder]);

  return [searchResult, setSearchQuery, searchQuery];
}

export const isSearchLoading = (search: SearchState): boolean => {
  return search.state === 'can_load' || search.state === 'loading';
};
